import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import $api from 'http/index'
import Notification from 'utils/Notifications'
import {
  Icon,
  PageHeader,
  Skeleton,
  EmptyState,
  ErrorState,
  Card,
  Badge,
  UnvButton,
  UnvInput,
  UnvModal,
} from 'components'

import styles from './ExamSessionsPage.module.scss'

const RU_MONTHS = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь']
const WEEKDAY_SHORT = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']

const isoDate = (d) => {
  const dt = new Date(d)
  const y = dt.getFullYear()
  const m = String(dt.getMonth() + 1).padStart(2, '0')
  const day = String(dt.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const emptyForm = {
  subject: '',
  group: '',
  date: '',
  time: '09:00',
  room: '',
  exam_type: 'exam',
  proctor: '',
  proctoring: false,
}

const ExamSessionsPage = () => {
  const { t } = useTranslation('common')
  const [list, setList] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [subjects, setSubjects] = useState([])
  const [groups, setGroups] = useState([])
  const [rooms, setRooms] = useState([])
  const [teachers, setTeachers] = useState([])
  const [anchor, setAnchor] = useState(new Date())
  const [createOpen, setCreateOpen] = useState(false)
  const [dayDrawer, setDayDrawer] = useState(null) // ISO date string
  const [form, setForm] = useState(emptyForm)
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })

  const load = async () => {
    setLoading(true)
    setError(false)
    try {
      const res = await $api.get('/academic/exam-sessions/')
      setList(res.data?.results || res.data || [])
    } catch {
      setError(true)
      setList([])
    } finally {
      setLoading(false)
    }
  }

  const loadAux = async () => {
    try { const r = await $api.get('/academic/subjects/'); setSubjects(r.data?.results || r.data || []) } catch {}
    try { const r = await $api.get('/groups/'); setGroups(r.data?.results || r.data || []) } catch {}
    try { const r = await $api.get('/academic/rooms/'); setRooms(r.data?.results || r.data || []) } catch {}
    try { const r = await $api.get('/employees/?status=active&page_size=200'); setTeachers(r.data?.results || r.data || []) } catch {}
  }

  useEffect(() => { load(); loadAux() }, [])

  // build month grid 7x6
  const monthCells = useMemo(() => {
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1)
    const offset = (first.getDay() + 6) % 7 // Mon = 0
    const start = new Date(first)
    start.setDate(first.getDate() - offset)
    const cells = []
    for (let i = 0; i < 42; i++) {
      const d = new Date(start)
      d.setDate(start.getDate() + i)
      cells.push(d)
    }
    return cells
  }, [anchor])

  // group exams by ISO date
  const examsByDay = useMemo(() => {
    const m = {}
    list.forEach((e) => {
      const iso = e.date || (e.start_at ? e.start_at.slice(0, 10) : null)
      if (!iso) return
      if (!m[iso]) m[iso] = []
      m[iso].push(e)
    })
    return m
  }, [list])

  // conflict detection: same room+time on same day
  const conflictDays = useMemo(() => {
    const set = new Set()
    Object.entries(examsByDay).forEach(([iso, exams]) => {
      const buckets = {}
      exams.forEach((e) => {
        const key = `${e.room_name || e.room?.name || e.room || ''}|${(e.start_at || '').slice(11, 16)}`
        buckets[key] = (buckets[key] || 0) + 1
      })
      if (Object.values(buckets).some((c) => c > 1)) set.add(iso)
    })
    return set
  }, [examsByDay])

  const create = async () => {
    if (!form.subject || !form.group || !form.date) {
      setNotify({ isOpen: true, message: t('sessions.fill_required', 'Заполните обязательные поля'), type: 'warning', sound: 'warning' })
      return
    }
    try {
      const start_at = `${form.date}T${form.time}:00`
      await $api.post('/academic/exam-sessions/', {
        subject: form.subject,
        group: form.group,
        start_at,
        room: form.room || null,
        proctoring: form.proctoring,
        // TODO API: add exam_type and proctor on backend
        exam_type: form.exam_type,
        proctor: form.proctor || null,
      })
      setNotify({ isOpen: true, message: t('sessions.session_created', 'Экзамен создан'), type: 'success', sound: 'success' })
      setCreateOpen(false)
      setForm(emptyForm)
      load()
    } catch {
      setNotify({ isOpen: true, message: t('sessions.error', 'Ошибка'), type: 'error', sound: 'error' })
    }
  }

  const exportXlsx = () => {
    // TODO API: backend export endpoint
    setNotify({ isOpen: true, message: t('sessions.export_started', 'Экспорт запущен'), type: 'info' })
  }

  const openDay = (iso) => {
    setDayDrawer(iso)
  }

  const navigateMonth = (dir) => {
    const d = new Date(anchor)
    d.setMonth(d.getMonth() + dir)
    setAnchor(d)
  }

  const monthLabel = `${RU_MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}`
  const todayIso = isoDate(new Date())

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('sessions.title', 'Сессии и экзамены')}
        subtitle={t('sessions.subtitle', 'Календарное планирование экзаменов и зачётов')}
        actions={
          <>
            <UnvButton variant="ghost" iconLeft="download" onClick={exportXlsx}>{t('sessions.export', 'Экспорт XLSX')}</UnvButton>
            <UnvButton variant="primary" iconLeft="plus" onClick={() => setCreateOpen(true)}>{t('sessions.new_session', 'Создать экзамен')}</UnvButton>
          </>
        }
      />

      <div className={styles.toolbar} role="toolbar">
        <button type="button" className={styles.iconBtn} onClick={() => navigateMonth(-1)} aria-label={t('sessions.prev_month', 'Прошлый месяц')}>
          <Icon name="chevron-left" size={16} />
        </button>
        <div className={styles.monthLabel}>{monthLabel}</div>
        <button type="button" className={styles.iconBtn} onClick={() => navigateMonth(1)} aria-label={t('sessions.next_month', 'Следующий месяц')}>
          <Icon name="chevron-right" size={16} />
        </button>
        <UnvButton size="sm" variant="ghost" onClick={() => setAnchor(new Date())}>{t('schedule.today', 'Сегодня')}</UnvButton>
      </div>

      {loading ? (
        <Card><Skeleton variant="table" rows={6} cols={7} /></Card>
      ) : error ? (
        <ErrorState title={t('sessions.load_failed', 'Не удалось загрузить')} onRetry={load} />
      ) : (
        <Card padding="none" className={styles.calendarCard}>
          <div className={styles.calendar} role="grid" aria-label={monthLabel}>
            {WEEKDAY_SHORT.map((w) => (
              <div key={w} className={styles.dowHeader} role="columnheader">{w}</div>
            ))}
            {monthCells.map((d) => {
              const iso = isoDate(d)
              const inMonth = d.getMonth() === anchor.getMonth()
              const exams = examsByDay[iso] || []
              const visible = exams.slice(0, 3)
              const overflow = exams.length - 3
              const isToday = iso === todayIso
              const hasConflict = conflictDays.has(iso)
              return (
                <button
                  key={iso}
                  type="button"
                  className={[
                    styles.dayCell,
                    !inMonth ? styles.outMonth : '',
                    isToday ? styles.today : '',
                    exams.length === 0 ? styles.empty : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => openDay(iso)}
                  aria-label={`${d.getDate()} ${RU_MONTHS[d.getMonth()]} — ${exams.length} ${t('sessions.exams_n', 'экзаменов')}`}
                >
                  <div className={styles.dayHeader}>
                    <span className={styles.dayNum}>{d.getDate()}</span>
                    {hasConflict && <span className={styles.conflictDot} aria-label={t('sessions.conflict', 'Конфликт')} />}
                  </div>
                  <div className={styles.examList}>
                    {visible.map((e) => (
                      <div key={e.id} className={styles.examPill}>
                        <span className={styles.pillTime}>{(e.start_at || '').slice(11, 16)}</span>
                        <span className={styles.pillText}>{e.subject_name || e.subject?.name || '—'}</span>
                      </div>
                    ))}
                    {overflow > 0 && (
                      <div className={styles.overflow}>+{overflow}</div>
                    )}
                  </div>
                </button>
              )
            })}
          </div>
        </Card>
      )}

      {!loading && !error && list.length === 0 && (
        <Card>
          <EmptyState
            iconName="calendar"
            title={t('sessions.no_sessions', 'Нет экзаменов')}
            description={t('sessions.no_sessions_desc', 'Создайте первый экзамен, нажав «Создать экзамен»')}
          />
        </Card>
      )}

      {/* Day drawer */}
      <UnvModal
        open={!!dayDrawer}
        onClose={() => setDayDrawer(null)}
        title={dayDrawer ? `${t('sessions.day_label', 'Экзамены на')} ${dayDrawer}` : ''}
      >
        {dayDrawer && (
          <div className={styles.dayDrawer}>
            {(examsByDay[dayDrawer] || []).length === 0 ? (
              <EmptyState iconName="calendar" title={t('sessions.no_exams_day', 'В этот день экзаменов нет')} description="" />
            ) : (
              (examsByDay[dayDrawer] || []).map((e) => (
                <Card key={e.id} padding="sm" className={styles.dayExamCard}>
                  <div className={styles.dayExamHead}>
                    <strong>{e.subject_name || e.subject?.name || '—'}</strong>
                    <Badge variant={e.status === 'closed' ? 'success' : e.status === 'in_progress' ? 'warning' : 'muted'} size="sm">
                      {e.status || 'scheduled'}
                    </Badge>
                  </div>
                  <div className={styles.dayExamMeta}>
                    <span><Icon name="clock" size={12} /> {(e.start_at || '').slice(11, 16)}</span>
                    <span><Icon name="group" size={12} /> {e.group_name || e.group?.name || '—'}</span>
                    <span><Icon name="building" size={12} /> {e.room_name || e.room?.name || '—'}</span>
                  </div>
                </Card>
              ))
            )}
            <UnvButton fullWidth variant="primary" iconLeft="plus" onClick={() => { setForm({ ...emptyForm, date: dayDrawer }); setCreateOpen(true); setDayDrawer(null) }}>
              {t('sessions.add_for_day', 'Добавить экзамен на эту дату')}
            </UnvButton>
          </div>
        )}
        <UnvModal.Footer>
          <UnvButton variant="ghost" onClick={() => setDayDrawer(null)}>{t('common.close', 'Закрыть')}</UnvButton>
        </UnvModal.Footer>
      </UnvModal>

      {/* Create form */}
      <UnvModal open={createOpen} onClose={() => setCreateOpen(false)} title={t('sessions.new_session_dialog', 'Новый экзамен')} size="md">
        <div className={styles.form}>
          <label className={styles.lbl}>{t('sessions.subject_label', 'Дисциплина')}</label>
          <select className={styles.select} value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}>
            <option value="">—</option>
            {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>

          <label className={styles.lbl}>{t('sessions.group_label', 'Группа')}</label>
          <select className={styles.select} value={form.group} onChange={(e) => setForm((f) => ({ ...f, group: e.target.value }))}>
            <option value="">—</option>
            {groups.map((g) => <option key={g.id} value={g.id}>{g.name || g.title}</option>)}
          </select>

          <div className={styles.row2}>
            <div>
              <label className={styles.lbl}>{t('sessions.date_label', 'Дата')}</label>
              <UnvInput type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
            </div>
            <div>
              <label className={styles.lbl}>{t('sessions.time_label', 'Время')}</label>
              <UnvInput type="time" value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} />
            </div>
          </div>

          <div className={styles.row2}>
            <div>
              <label className={styles.lbl}>{t('sessions.room_label', 'Аудитория')}</label>
              <select className={styles.select} value={form.room} onChange={(e) => setForm((f) => ({ ...f, room: e.target.value }))}>
                <option value="">—</option>
                {rooms.map((r) => <option key={r.id} value={r.id}>{r.name || r.number}</option>)}
              </select>
            </div>
            <div>
              <label className={styles.lbl}>{t('sessions.type_label', 'Тип')}</label>
              <select className={styles.select} value={form.exam_type} onChange={(e) => setForm((f) => ({ ...f, exam_type: e.target.value }))}>
                <option value="exam">{t('sessions.type_exam', 'Экзамен')}</option>
                <option value="zachet">{t('sessions.type_zachet', 'Зачёт')}</option>
              </select>
            </div>
          </div>

          <label className={styles.lbl}>{t('sessions.proctor_label', 'Проктор')}</label>
          <select className={styles.select} value={form.proctor} onChange={(e) => setForm((f) => ({ ...f, proctor: e.target.value }))}>
            <option value="">—</option>
            {teachers.map((tch) => <option key={tch.id} value={tch.id}>{tch.full_name || tch.name}</option>)}
          </select>

          <label className={styles.checkLbl}>
            <input
              type="checkbox"
              checked={form.proctoring}
              onChange={(e) => setForm((f) => ({ ...f, proctoring: e.target.checked }))}
            />
            {t('sessions.proctoring_label', 'Включить прокторинг')}
          </label>
        </div>
        <UnvModal.Footer>
          <UnvButton variant="ghost" onClick={() => setCreateOpen(false)}>{t('common.cancel', 'Отмена')}</UnvButton>
          <UnvButton variant="primary" iconLeft="check" onClick={create}>{t('sessions.create_btn', 'Создать')}</UnvButton>
        </UnvModal.Footer>
      </UnvModal>

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

export default ExamSessionsPage
